/*
 * Copyright (c) 2025.
 * This code and its derivatives belong to BETICA LTD and may not be copied, reproduced, amended or used in any way without the permission of BETICA LTD
 * @author torrejonv
 *
 */

/**
 * @file auth-selector.js
 * @description Redux selectors for accessing auth state data
 */

import { createSelector } from "@reduxjs/toolkit";

/**
 * @function getAuthSelector
 * @description Selector that returns the auth data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthSelector = createSelector(
  (state) => state.auth,
  (auth) => auth || {},
);

/**
 * @function getAuthAccountId
 * @description Selector that returns the auth account id data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthAccountId = createSelector(
  (state) => state.auth?.accountId,
  (accountId) => accountId,
);

/**
 * @function getAuthCurrencyCode
 * @description Selector that returns the auth currency code data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthCurrencyCode = createSelector(
  (state) => state.auth?.currencyCode,
  (currencyCode) => currencyCode,
);

/**
 * @function getAuthError
 * @description Selector that returns the auth error data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthError = createSelector(
  (state) => state.auth?.error,
  (error) => error,
);

/**
 * @function getAuthDesktopView
 * @description Selector that returns the auth desktop view data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthDesktopView = createSelector(
  (state) => state.auth?.desktopView,
  // View based on CMS data...
  (desktopView) => desktopView,
);

/**
 * @function getAuthLanguage
 * @description Selector that returns the auth language data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthLanguage = createSelector(
  (state) => state.auth?.language,
  (language) => language,
);

/**
 * @function getAuthLoading
 * @description Selector that returns the auth loading data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthLoading = createSelector(
  (state) => state.auth?.loading,
  (loading) => loading,
);

/**
 * @function getAuthLoggedIn
 * @description Selector that returns the auth logged in data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthLoggedIn = createSelector(
  (state) => !!state.auth?.loggedIn,
  (loggedIn) => loggedIn,
);

/**
 * @function getAuthMobileView
 * @description Selector that returns the auth mobile view data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthMobileView = createSelector(
  (state) => state.auth?.mobileView,
  // View based on CMS data...
  (mobileView) => mobileView,
);

/**
 * @function getAuthMobileTheme
 * @description Selector that returns the auth mobile theme data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthMobileTheme = createSelector(
  (state) => state.auth?.mobileTheme,
  (mobileTheme) => mobileTheme,
);

/**
 * @function getAuthPriceFormat
 * @description Selector that returns the auth price format data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthPriceFormat = createSelector(
  (state) => state.auth?.priceFormat,
  (priceFormat) => priceFormat,
);

/**
 * @function getAuthRememberedUsername
 * @description Selector that returns the auth remembered username data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthRememberedUsername = createSelector(
  (state) => state.auth?.rememberedUsername,
  (rememberedUsername) => rememberedUsername,
);

/**
 * @function getAuthTimezoneOffset
 * @description Selector that returns the auth timezone offset data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthTimezoneOffset = createSelector(
  (state) => state.auth?.timezoneOffset,
  (timezoneOffset) => timezoneOffset,
);

/**
 * @function getAuthToken
 * @description Selector that returns the auth token data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthToken = createSelector(
  (state) => state.auth?.authToken,
  (authToken) => authToken,
);

/**
 * @function getAuthUsername
 * @description Selector that returns the auth username data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthUsername = createSelector(
  (state) => state.auth?.username,
  (username) => username,
);

/**
 * @function getAuthIsIframe
 * @description Selector that returns the auth is iframe data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthIsIframe = createSelector(
  (state) => state.auth?.isIframe,
  (isIframe) => isIframe,
);

/**
 * @function getAuthLoginURL
 * @description Selector that returns the auth login u r l data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthLoginURL = createSelector(
  (state) => state.auth?.authLoginURL,
  (authLoginURL) => authLoginURL,
);

/**
 * @function getAuthReferrer
 * @description Selector that returns the referrer URL data from state or document.referrer if state value not set
 * @param {Object} state - Redux state
 * @returns {string|null} The referrer URL, document.referrer as fallback, or null if not set
 */
export const getAuthReferrer = createSelector(
  [(state) => state.auth?.referrer, () => (typeof document !== "undefined" ? document.referrer : null)],
  (stateReferrer, documentReferrer) => stateReferrer || documentReferrer || null,
);

/**
 * @function getAuthIsSplitModePreferred
 * @description Selector that returns the auth is split mode preferred data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthIsSplitModePreferred = createSelector(
  (state) => state.auth?.isSplitModePreferred,
  (getAuthIsSplitModePreferred) => getAuthIsSplitModePreferred,
);

/**
 * @function getAuthTill
 * @description Selector that returns the auth till data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthTill = createSelector(
  (state) => state.auth?.tillAuth,
  (tillAuth) => tillAuth,
);

/**
 * @function getAuthIFrameQueryParamsProcessed
 * @description Selector that returns the auth i frame query params processed data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthIFrameQueryParamsProcessed = createSelector(
  (state) => !!state.auth?.authIFrameQueryParamsProcessed,
  (loggedIn) => loggedIn,
);

/**
 * @function getAuthPlayerRequestedDesktopView
 * @description Selector that returns the auth player requested desktop view data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthPlayerRequestedDesktopView = createSelector(
  (state) => state.auth?.playerRequestedDesktopView,
  // View based on CMS data...
  (playerRequestedDesktopView) => playerRequestedDesktopView,
);

/**
 * @function getAuthPlayerRequestedMobileView
 * @description Selector that returns the auth player requested mobile view data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthPlayerRequestedMobileView = createSelector(
  (state) => state.auth?.playerRequestedMobileView,
  // View based on CMS data...
  (playerRequestedMobileView) => playerRequestedMobileView,
);

/**
 * @function getAuthPlayerRequestedPriceFormat
 * @description Selector that returns the auth player requested price format data from state
 * @param {Object} state - Redux state
 * @returns {*} Selected state data
 */
export const getAuthPlayerRequestedPriceFormat = createSelector(
  (state) => state.auth?.playerRequestedPriceFormat,
  // View based on CMS data...
  (playerRequestedPriceFormat) => playerRequestedPriceFormat,
);
